// The widgets that read the user's own computer, through its runner: a Project
// file (a note or a checklist kept in step with a file in their project
// folders), an Agent job (a request their agent runs there), Needs you (what is
// waiting on them), AI usage (Claude Code and Codex on that computer) and
// Project activity (git in the project folders). The user adds them from the
// gallery; an agent never does, and a shared view shows only that each is private.

import { useEffect, useState } from "react";
import { sessionName } from "../../shared/sessionName";
import { BellRing, Check, FolderOpen, GitCommitHorizontal, Lock, PlayCircle, Plus, ScrollText, Zap } from "lucide-react";
import { cleanAuto } from "../../shared/brief";
import { addFileTask, readFileTasks, setFileTask, type FileTask } from "../../shared/filetasks";
import { shortAgo, timeAgo } from "../../shared/format";
import type { AgentLimits, AgentState, FileCopy, RunnerState, WidgetItem } from "../../shared/types";
import { useSize } from "../charts/charts";
import { api } from "../lib/api";
import { cx } from "../lib/util";
import { Linked } from "../lib/Linked";
import { updateItem } from "../state/actions";
import { set, toast, useStore } from "../state/store";
import { needLine } from "./agents";
import type { WP } from "./data";
import { quarter } from "./layout";
import { usageAlert, usageFit } from "./plans";
import { Markdown } from "./markdown";
import { useTick } from "./tick";
import { useSample } from "./WidgetTile";
import { Custom } from "./custom";
import { useJobRunning } from "./job";
import { flush } from "../state/sync";
import { readStoredJson, writeStoredJson } from "../lib/stored";

const nameOf = (path: string) => path.replace(/[\\/]+$/, "").split(/[\\/]/).pop() || path;
/** "just now", or "5m ago" */
const ago = (t: number) => (shortAgo(t) === "now" ? "just now" : `${shortAgo(t)} ago`);
const openSettings = (item: WidgetItem) => set({ selection: [item.id], inspect: true, editing: null });

function Private({ what }: { what: string }) {
  return (
    <div className="pw pw-empty">
      <Lock size={16} />
      <b>Private</b>
      <span>{what} is only for the board's owner.</span>
    </div>
  );
}

function Empty({ icon, title, sub, children }: { icon: React.ReactNode; title: string; sub?: React.ReactNode; children?: React.ReactNode }) {
  return (
    <div className="pw pw-empty">
      <span className="pw-ico">{icon}</span>
      <b>{title}</b>
      {sub && <span>{sub}</span>}
      {children && <div className="empty-actions">{children}</div>}
    </div>
  );
}

const linkComputer = () => set({ panel: "connect", panelArg: { computers: true } });
const chooseFolders = () => set({ panel: "settings", panelArg: { tab: "computer" } });

/** The first step for a widget that reads the user's computer while none is linked: what it shows, and the one button. */
function LinkFirst({ icon, title, sub }: { icon: React.ReactNode; title: string; sub: string }) {
  return (
    <Empty icon={icon} title={title} sub={sub}>
      <button className="btn btn-sm btn-primary" onClick={linkComputer} data-interactive>
        Link your computer
      </button>
    </Empty>
  );
}

/** Whether a computer was ever linked to the board: on now, or paired and off. */
const isLinked = (r: RunnerState | null | undefined) => !!r?.online || (r?.computers ?? []).length > 0;

/** Whether the computer is linked with a runner new enough for these widgets, and why not when it is not. */
function useComputer(): { runner: RunnerState | null; ready: boolean; why: string | null } {
  const runner = useStore((s) => s.runner);
  if (!runner?.online) return { runner, ready: false, why: isLinked(runner) ? `${runner?.computers?.[0]?.host ?? "Your computer"} is off. It shows here again once it is on.` : "Link your computer first." };
  if (!runner.cli) return { runner, ready: false, why: `This needs the newest runner, which reaches ${runner.host ?? "your computer"} by itself within a day.` };
  return { runner, ready: true, why: null };
}

// ---------------------------------------------------------------------------
// Project file

/** The chosen folder a file is in, as the computer last said, for whether it may be changed from here. */
function folderOf(runner: RunnerState | null, path: string) {
  const norm = (p: string) => p.replace(/\\/g, "/").replace(/\/+$/, "").toLowerCase();
  const p = norm(path);
  return (runner?.folders ?? []).find((f) => p === norm(f.path) || p.startsWith(norm(f.path) + "/")) ?? null;
}

export function ProjectFile({ item, readOnly }: WP) {
  useTick(60_000);
  const path = String(item.props.path ?? "");
  const mode = item.props.mode === "tasks" ? "tasks" : "note";
  const sample = useSample();
  const kept = useStore((s) => (path ? s.files[path] : undefined)) as FileCopy | undefined;
  const copy = sample?.files?.[path] ?? kept;
  const computer = useComputer();
  // the gallery draws it with an example file, as if from a linked computer
  const { runner, ready, why } = sample ? { runner: sample.runner ?? null, ready: true, why: null } : computer;
  const [editing, setEditing] = useState<string | null>(null);
  const [adding, setAdding] = useState("");
  const [busy, setBusy] = useState(false);
  if (readOnly && !sample) return <Private what="A file from the owner's computer" />;
  if (!path)
    return (
      <Empty icon={<ScrollText />} title="Choose a file" sub="A note or a checklist from your project folders, kept in step with the file on your computer.">
        <button className="btn btn-sm btn-primary" onClick={() => openSettings(item)} data-interactive>
          Choose a file
        </button>
      </Empty>
    );
  const folder = folderOf(runner, path);
  const canWrite = ready && !!folder?.write;
  const host = runner?.host ?? "your computer";
  const write = async (text: string) => {
    setBusy(true);
    try {
      const r = await api<{ mtime: number | null; replaced: boolean }>("/api/files/write", { body: { path, text, base: copy?.mtime ?? null } });
      // the board shows it at once; the computer's own copy follows
      set((s) => ({ files: { ...s.files, [path]: { ...(s.files[path] ?? { path, at: Date.now() }), text, mtime: r.mtime ?? s.files[path]?.mtime ?? null, at: Date.now() } } }));
      if (r.replaced) toast({ level: "info", title: "Saved", body: `The file had changed on ${host} since the board last had it. That version is kept as a backup there.` });
      return true;
    } catch (e) {
      toast({ level: "warn", title: "Not saved", body: (e as Error).message });
      return false;
    } finally {
      setBusy(false);
    }
  };
  const foot = (
    <div className="pw-foot">
      <span title={path}>{nameOf(path)}</span>
      <span>
        {!ready && copy ? `${host} is off: the copy from ${timeAgo(copy.at)}` : copy?.mtime ? `changed ${timeAgo(copy.mtime)}` : ""}
        {ready && folder && !folder.write ? " · read only" : ""}
      </span>
    </div>
  );
  if (!copy) return <Empty icon={<ScrollText />} title={nameOf(path)} sub={ready ? `Reading it on ${host}…` : (why ?? "")} />;
  if (copy.missing) return <Empty icon={<ScrollText />} title={`There is no ${nameOf(path)}`} sub={`It is not on ${host} any more. Choose another in its settings.`} />;
  if (copy.error || copy.text === null) return <Empty icon={<ScrollText />} title={nameOf(path)} sub={copy.error ?? "It could not be read."} />;
  const text = copy.text;
  if (mode === "tasks") {
    const tasks = readFileTasks(text);
    const toggle = (t: FileTask) => {
      const next = setFileTask(text, t, !t.done);
      if (next === null) toast({ level: "warn", title: "That line changed", body: "The file changed on your computer. Try again." });
      else void write(next);
    };
    return (
      <div className="pw pw-tasks" data-scroll>
        {tasks.length === 0 && <span className="pw-note">No checklist in this file yet. Add a task and it starts one.</span>}
        <ul className="pw-task-list">
          {tasks.map((t) => (
            <li key={t.line} className={cx(t.done && "is-done")} style={{ paddingLeft: t.depth * 16 }}>
              <button className="pw-box" disabled={!canWrite || busy} aria-label={t.done ? `Untick ${t.text}` : `Tick ${t.text}`} onClick={() => toggle(t)} data-interactive>
                {t.done && <Check size={12} strokeWidth={3} />}
              </button>
              <span>
                <Linked text={t.text} />
              </span>
            </li>
          ))}
        </ul>
        {canWrite && (
          <form
            className="pw-add"
            onSubmit={(e) => {
              e.preventDefault();
              if (!adding.trim()) return;
              void write(addFileTask(text, adding)).then((ok) => ok && setAdding(""));
            }}
            data-interactive
          >
            <Plus size={13} />
            <input value={adding} placeholder="Add a task" onChange={(e) => setAdding(e.target.value)} onKeyDown={(e) => e.stopPropagation()} />
          </form>
        )}
        {foot}
      </div>
    );
  }
  if (editing !== null)
    return (
      <div className="pw pw-edit" data-interactive>
        <textarea value={editing} onChange={(e) => setEditing(e.target.value)} onKeyDown={(e) => e.stopPropagation()} autoFocus />
        <div className="inline-actions">
          <button className="btn btn-sm btn-primary" disabled={busy} onClick={() => void write(editing).then((ok) => ok && setEditing(null))}>
            {busy ? <span className="spinner" /> : "Save to the file"}
          </button>
          <button className="btn btn-sm btn-ghost" disabled={busy} onClick={() => setEditing(null)}>
            Cancel
          </button>
        </div>
      </div>
    );
  return (
    <div className="pw pw-note-view" data-scroll>
      {canWrite && (
        <button className="btn btn-xs pw-edit-btn" onClick={() => setEditing(text)} data-interactive>
          Edit
        </button>
      )}
      {text.trim() ? <Markdown text={text} /> : <span className="pw-note">This file is empty.</span>}
      {foot}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Agent job

export function AgentJob({ item, stream, readOnly }: WP) {
  useTick(60_000);
  const a = cleanAuto(item.props.auto);
  const { runner, ready, why } = useComputer();
  const sample = useSample();
  const [busy, setBusy] = useState(false);
  // running now on the user's computer: it says so until the answer is in
  const running = useJobRunning(item.id) > 0 && !sample;
  if (readOnly && !sample) return <Private what="An agent job on the owner's computer" />;
  const text = stream?.text ?? (typeof stream?.value === "string" ? stream.value : "");
  // the design its agent made to fit the answer (an inbox, a scoreboard), drawn by the widget kit from what it writes
  const designed = typeof item.props.html === "string" && !!item.props.html.trim() && !!stream?.updatedAt;
  const run = async () => {
    setBusy(true);
    try {
      await flush();
      await api(`/api/briefs/${encodeURIComponent(item.id)}/run`, { body: {} });
      toast({ level: "success", title: "Running it now", body: "The answer shows here when your agent has it." });
    } catch (e) {
      toast({ level: "warn", title: "Could not run it", body: (e as Error).message });
    } finally {
      setBusy(false);
    }
  };
  const when = !a?.on ? null : a.every === "manual" ? "When you run it" : a.every === "week" ? `${["Sundays", "Mondays", "Tuesdays", "Wednesdays", "Thursdays", "Fridays", "Saturdays"][a.day]} at ${a.time}` : `Every day at ${a.time}`;
  if (!a?.prompt)
    return (
      <Empty icon={<PlayCircle />} title="Your agent runs this" sub="Say what it should do, and when: every day, every week, or when you press Run. It runs on your computer, with your project folders.">
        <button className="btn btn-sm btn-primary" onClick={() => openSettings(item)} data-interactive>
          Set it up
        </button>
      </Empty>
    );
  const meta = (
    <div className="br-meta">
      {running ? (
        <span className="pw-running">
          <span className="spinner" /> Running now
        </span>
      ) : (
        <span className={cx(stream?.error && "is-old")} title={stream?.error ?? undefined}>
          {stream?.updatedAt ? `Ran ${ago(stream.updatedAt)}` : "Not run yet"}
          {when ? ` · ${when}` : ""}
          {stream?.error ? ". The last run failed" : ""}
        </span>
      )}
      <button className="br-again" disabled={busy || running || !ready} title={running ? "It is running now" : (why ?? "Run it now")} onClick={() => void run()} data-interactive>
        <PlayCircle /> Run now
      </button>
    </div>
  );
  if (designed)
    return (
      <div className="pw-job-designed">
        {meta}
        <div className="pw-job-design">
          <Custom item={item} stream={stream} readOnly={readOnly} />
        </div>
      </div>
    );
  return (
    <div className="br pw-job" data-scroll>
      {meta}
      {running && !text.trim() ? (
        <span className="pw-note">Your agent is on it. The answer shows here when it is done.</span>
      ) : text.trim() ? (
        <Markdown text={text} />
      ) : ready ? (
        <span className="pw-note">Press Run now, or wait for its time.</span>
      ) : (
        <span className="pw-note">
          {why}{" "}
          {!isLinked(runner) && (
            <button className="link-btn" onClick={linkComputer} data-interactive>
              Link your computer
            </button>
          )}
        </span>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Needs you

interface Waiting {
  key: string;
  who: string;
  what: string;
  where: string | null;
  at: number;
  level: "warn" | "bad";
}

export function NeedsYou({ readOnly }: WP) {
  useTick(30_000);
  const computer = useComputer();
  const sample = useSample();
  const runner = sample?.runner ?? computer.runner;
  const storeAgents = useStore((s) => s.agents);
  const agents = sample?.agents ?? storeAgents;
  const streams = useStore((s) => s.streams);
  const items = useStore((s) => s.items);
  if (readOnly && !sample) return <Private what="What is waiting on the owner" />;
  const list: Waiting[] = [];
  const linked = !!sample || isLinked(runner);
  const many = new Set((runner?.sessions ?? []).map((x) => x.host)).size > 1;
  for (const x of runner?.sessionsOn ? (runner.sessions ?? []) : [])
    if (x.state === "needs_you")
      list.push({
        key: `s:${x.host ?? ""}:${x.id}`,
        who: sessionName(x),
        what: needLine(x),
        where: [x.agent === "codex" ? "Codex" : "Claude Code", x.app !== "Claude Code" && x.app !== "Codex" ? x.app : null, x.title ? x.project : null, many ? x.host : null].filter(Boolean).join(" · ") || null,
        at: x.at,
        level: "warn",
      });
  const dayAgo = Date.now() - 24 * 3_600_000;
  for (const st of Object.values(agents) as AgentState[])
    if ((st.status === "needs_you" || st.status === "failed") && st.updatedAt > dayAgo)
      list.push({ key: `a:${st.name}`, who: st.name, what: st.status === "failed" ? `Failed${st.task ? `: ${st.task}` : ""}` : `Needs you${st.task ? `: ${st.task}` : ""}`, where: st.detail, at: st.updatedAt, level: st.status === "failed" ? "bad" : "warn" });
  for (const it of Object.values(items))
    if (it.kind === "widget" && (it.type === "brief" || it.type === "job") && it.stream) {
      const s = streams[it.stream];
      if (s?.error && (s.errorAt ?? 0) > dayAgo) list.push({ key: `w:${it.id}`, who: it.title || (it.type === "job" ? "Agent job" : "Brief"), what: it.type === "job" ? "The job did not run" : "The brief was not written", where: s.error, at: s.errorAt ?? 0, level: "bad" });
    }
  list.sort((a, b) => b.at - a.at);
  return (
    <div className="pw pw-needs" data-scroll>
      {!sample && !runner?.sessionsOn && runner?.online && (
        <div className="pw-hint">
          <span>Your Claude Code and Codex sessions waiting for you can show here too.</span>
          <button className="btn btn-xs" onClick={() => void api("/api/runner/sessions", { body: { on: true } }).catch(() => {})} data-interactive>
            Show my sessions
          </button>
        </div>
      )}
      {list.length === 0 && !linked ? (
        <LinkFirst icon={<BellRing />} title="What is waiting on you" sub="Link your computer, and any Claude Code or Codex session that asks you a question or wants your OK shows here, with agents that ask for you and jobs that fail." />
      ) : list.length === 0 ? (
        <Empty icon={<BellRing />} title="Nothing is waiting on you" sub="Sessions waiting for you, agents that ask for you and jobs that fail show here." />
      ) : (
        <ul className="pw-rows">
          {list.slice(0, 20).map((w) => (
            <li key={w.key} className={cx("pw-row", `is-${w.level}`)}>
              <span className="dot" />
              <span className="pw-row-what">
                <b>{w.what}</b>
                <span>
                  {w.who}
                  {w.where ? (
                    <>
                      {" · "}
                      <Linked text={w.where} />
                    </>
                  ) : (
                    ""
                  )}
                </span>
              </span>
              <span className="pw-row-at">{w.at ? shortAgo(w.at) : ""}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// AI usage

/** When a window starts again: "in 1h 10m" today, or the day and time when it is further off. */
function resetText(at: number | null): string {
  if (!at) return "";
  const ms = at - Date.now();
  if (ms <= 0) return "renewed";
  const m = Math.round(ms / 60_000);
  if (m < 60) return `resets in ${m}m`;
  if (m < 12 * 60) return `resets in ${Math.floor(m / 60)}h${m % 60 ? ` ${m % 60}m` : ""}`;
  const d = new Date(at);
  return `resets ${d.toLocaleDateString(undefined, { weekday: "short" })} ${d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}`;
}

/** What is left of a window, in percent: a window past its reset is whole again. */
const leftOf = (w: { used: number; resetsAt: number | null }) => (w.resetsAt !== null && w.resetsAt <= Date.now() ? 100 : 100 - w.used);

/** The session and week windows under the user's alert line, keyed by their reset so each warns once a window. */
function lowWindows(limits: AgentLimits | undefined, below: number) {
  if (!below || !limits) return [];
  return (["claude", "codex"] as const).flatMap((k) =>
    (limits[k]?.windows ?? [])
      .filter((w) => (w.id === "session" || w.id === "week") && leftOf(w) < below)
      .map((w) => ({ key: `${k}:${w.id}:${w.resetsAt ?? 0}`, app: k === "claude" ? "Claude Code" : "Codex", window: w.id === "session" ? "this session" : "this week", left: leftOf(w), sub: resetText(w.resetsAt) || "resets soon" })),
  );
}

/** How full a ring is: what is left of the window, in the colour of how much that is. */
function Ring({ left, label, sub, low }: { left: number; label: string; sub: string; /** under the user's alert line */ low?: boolean }) {
  // the line is thin, so the ring reaches nearly to the edge of its box
  const r = 29;
  const c = 2 * Math.PI * r;
  const tone = low || left < 20 ? "bad" : left >= 50 ? "ok" : "warn";
  return (
    <div className={cx("pw-ring", `is-${tone}`, Math.round(left) >= 100 && "is-full")} role="img" aria-label={`${label}: ${Math.round(left)}% left, ${sub}`}>
      <svg viewBox="0 0 64 64" aria-hidden>
        <circle className="pw-ring-track" cx="32" cy="32" r={r} />
        {left > 0 && <circle className="pw-ring-fill" cx="32" cy="32" r={r} strokeDasharray={`${(c * left) / 100} ${c}`} transform="rotate(-90 32 32)" />}
      </svg>
      <span className="pw-ring-n">
        <b>{Math.round(left)}%</b>
        <em>left</em>
      </span>
      <span className="pw-ring-label">{label}</span>
      <span className="pw-ring-sub">{sub}</span>
    </div>
  );
}

/**
 * What is left of the user's Claude Code and Codex plans: the current five-hour session and the week, as rings
 * that empty as they are used, with when each starts again. As each app last heard it from its own servers, on any
 * linked computer; nothing of a session.
 */
export function AiUsageW({ item, readOnly }: WP) {
  useTick(30_000);
  const { runner } = useComputer();
  const sample = useSample();
  const [ref, size] = useSize<HTMLDivElement>();
  const below = usageAlert(item.props);
  const lows = readOnly || sample ? [] : lowWindows(runner?.limits, below);
  const lowKey = lows.map((l) => l.key).join("|");
  // each window warns once, in this browser, until it starts again
  useEffect(() => {
    if (!lows.length) return;
    const store = `cb:usage-alert:${item.id}`;
    const seen = readStoredJson<string[]>(store, []); // storage may be off: warn anyway
    const fresh = lows.filter((l) => !seen.includes(l.key));
    for (const l of fresh) toast({ title: `${l.app}: ${Math.round(l.left)}% of ${l.window} left`, body: `Under your ${below}% alert. It ${l.sub}.`, level: "warn", who: "Context Board", key: `usage-alert:${l.key}`, itemId: item.id, ms: 15_000 });
    if (fresh.length) writeStoredJson(store, [...seen, ...fresh.map((l) => l.key)].slice(-20));
  }, [lowKey]); // eslint-disable-line react-hooks/exhaustive-deps
  if (readOnly && !sample) return <Private what="The owner's AI usage" />;
  const r = sample?.runner ?? runner;
  const limits = r?.limits ?? {};
  const computers = r?.computers ?? [];
  const ready = new Set(computers.flatMap((c) => c.agents.filter((a) => a.ready).map((a) => a.id)));
  const shown = (["claude", "codex"] as const).filter((k) => limits[k] || ready.has(k));
  const old = computers.find((c) => c.online && c.version && /^0\.(\d|1[0-3])\./.test(c.version));
  if (!r?.online && !computers.length)
    return <LinkFirst icon={<Zap />} title="What is left of your plans" sub="Link your computer and this shows how much of your Claude Code and Codex plans is left: this five-hour window and this week." />;
  // nothing read yet from either app: one note, not an empty row for each
  if (!shown.some((k) => limits[k]))
    return (
      <Empty
        icon={<Zap />}
        title="What is left of your plans"
        sub={old ? `The runner on ${old.host} is updating to the version that reads your limits. It takes a minute.` : `It shows after Claude Code or Codex next runs on ${r?.host ?? computers[0]?.host ?? "your computer"}.`}
      />
    );
  const apps = shown.map((k) => {
    const l = limits[k];
    const rings = (l?.windows ?? [])
      .filter((w) => w.id === "session" || w.id === "week")
      .map((w) => ({ id: w.id, label: w.label, sub: resetText(w.resetsAt), left: w.resetsAt !== null && w.resetsAt <= Date.now() ? 100 : 100 - w.used }));
    const more = (l?.windows ?? []).filter((w) => w.id !== "session" && w.id !== "week");
    return { k, rings, more };
  });
  // the rings and their words grow with the tile, arranged however lets them be biggest (plans.ts, usageFit)
  const fit = usageFit(size, apps.map((a) => ({ rings: a.rings, more: a.more.length > 0 })));
  return (
    <div ref={ref} className={cx("pw pw-limits", `is-${fit.id}`)} style={size.w > 0 ? ({ fontSize: quarter(fit.px), ["--apps" as string]: apps.length } as React.CSSProperties) : undefined} data-scroll>
      {apps.map(({ k, rings, more }) => (
        <section key={k} className="pw-limit">
          <span className="pw-limit-head">{k === "claude" ? "Claude Code" : "Codex"}</span>
          {rings.length ? (
            <div className="pw-rings">
              {rings.map((w) => (
                <Ring key={w.id} left={w.left} label={w.label} sub={w.sub} low={!sample && below > 0 && w.left < below} />
              ))}
            </div>
          ) : (
            <p className="pw-limit-none">{old ? `The runner on ${old.host} is updating to read this.` : "It reports its limits here after its next use."}</p>
          )}
          {more.length > 0 && <p className="pw-limit-more">{more.map((w) => `${w.label.replace(/^This week, /, "")}: ${Math.round(w.resetsAt !== null && w.resetsAt <= Date.now() ? 100 : 100 - w.used)}% left this week`).join(" · ")}</p>}
        </section>
      ))}
    </div>
  );
}

export function GitLog({ readOnly }: WP) {
  useTick(60_000);
  const computer = useComputer();
  const sample = useSample();
  const { runner, ready, why } = sample ? { runner: sample.runner ?? null, ready: true, why: null } : computer;
  if (readOnly && !sample) return <Private what="The owner's git activity" />;
  const repos = runner?.git;
  if (!repos?.length && !isLinked(runner))
    return <LinkFirst icon={<GitCommitHorizontal />} title="Today's commits" sub="Link your computer and choose your project folders. The commits in their git repos show here, with each branch and what is not committed yet." />;
  // no folders chosen, so there is nothing to read: say so, rather than "Reading…" for ever
  if (!repos?.length && ready && !(runner?.folders ?? []).length)
    return (
      <Empty icon={<FolderOpen />} title="Choose your project folders" sub={`The git repos in your project folders on ${runner?.host ?? "your computer"} show here: today's commits, the branch, and what is not committed yet.`}>
        <button className="btn btn-sm btn-primary" onClick={chooseFolders} data-interactive>
          Choose folders
        </button>
      </Empty>
    );
  if (!repos)
    return <Empty icon={<GitCommitHorizontal />} title="Git activity" sub={ready ? `Reading git in your project folders on ${runner?.host ?? "your computer"}…` : why} />;
  if (!repos.length)
    return (
      <Empty icon={<GitCommitHorizontal />} title="No git repos in your project folders" sub="A project folder that is a git repo, or holds some, shows its commits here.">
        <button className="btn btn-sm" onClick={chooseFolders} data-interactive>
          Your project folders
        </button>
      </Empty>
    );
  // from more than one computer, each repo says which it is on
  const many = new Set(repos.map((r) => r.host)).size > 1;
  return (
    <div className="pw pw-git" data-scroll>
      {repos.map((r) => (
        <section key={`${r.host ?? ""}:${r.path}`} className="pw-repo">
          <div className="pw-repo-head">
            <b>{r.name}</b>
            {r.branch && <span className="pw-chip">{r.branch}</span>}
            {many && r.host && <span className="pw-chip">{r.host}</span>}
            <span className="pw-repo-changes">{r.changes ? `${r.changes} not committed` : "all committed"}</span>
          </div>
          {r.commits.length === 0 ? (
            <span className="pw-note">Nothing committed today.</span>
          ) : (
            <ul className="pw-rows">
              {r.commits.slice(0, 8).map((c) => (
                <li key={c.hash} className="pw-row">
                  <span className="pw-row-what">
                    <b>{c.subject}</b>
                    <span>
                      {c.author} · {c.hash}
                    </span>
                  </span>
                  <span className="pw-row-at">{c.at ? shortAgo(c.at) : ""}</span>
                </li>
              ))}
            </ul>
          )}
        </section>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// choosing a file, in a Project file's settings

interface Entry {
  name: string;
  path: string;
  dir: boolean;
  write?: boolean;
}

export function FilePicker({ item }: { item: WidgetItem }) {
  const { runner, ready, why } = useComputer();
  const [at, setAt] = useState<string | null>(null);
  const [entries, setEntries] = useState<Entry[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!ready) return;
    let gone = false;
    setEntries(null);
    setError(null);
    api<{ entries: Entry[] }>("/api/runner/list-files", { body: { path: at } }).then(
      (r) => !gone && setEntries(r.entries),
      (e) => !gone && setError((e as Error).message),
    );
    return () => {
      gone = true;
    };
  }, [at, ready]);
  if (!ready) return <p className="side-hint">{why}</p>;
  if (!(runner?.folders ?? []).length)
    return (
      <p className="side-hint">
        Add a project folder first.{" "}
        <button className="link-btn" onClick={() => set({ panel: "settings", panelArg: { tab: "computer" } })}>
          Your project folders
        </button>
      </p>
    );
  const choose = (e: Entry) => updateItem(item.id, { props: { ...item.props, path: e.path }, title: item.title && item.title !== "Project file" ? item.title : nameOf(e.path).replace(/\.(md|markdown|txt)$/i, "") }, "Choose a file");
  // up one, and from a project folder back to the list of them
  const up = () => setAt(!at || (runner?.folders ?? []).some((f) => f.path === at) ? null : at.replace(/[\\/][^\\/]+[\\/]?$/, "") || null);
  return (
    <div className="pw-picker">
      <div className="pw-picker-at">
        {at ? (
          <button className="btn btn-xs btn-ghost" onClick={up}>
            ← Up
          </button>
        ) : null}
        <span title={at ?? undefined}>{at ? nameOf(at) : "Your project folders"}</span>
      </div>
      {error && <div className="callout is-warn">{error}</div>}
      {!entries && !error && <p className="side-hint">Reading it on {runner?.host ?? "your computer"}…</p>}
      <ul className="pw-picker-list">
        {(entries ?? []).map((e) => (
          <li key={e.path}>
            <button className={cx("pw-picker-item", e.dir && "is-dir", item.props.path === e.path && "is-on")} onClick={() => (e.dir ? setAt(e.path) : choose(e))}>
              {e.dir ? "📁" : "📄"} {e.name}
              {!at && <em>{e.write ? "read and write" : "read only"}</em>}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
